import { v4 as uuid } from 'uuid';
import { AccountInterface, RpcProvider } from 'starknet';
import { waitForTransactionReceipt } from '../../utils';
import { Decimal } from '../../datastructures';
import { Asset, InvokeResponse } from '../../interfaces';
import { Nullable } from '../../interfaces';
import { DecimalFormat } from '@tempusfinance/decimal';
import { addToast } from '../toast';
import { getConfigManager } from '../../config';
import { DeferredBatchCallAdapterContract } from '../../services/contracts/DeferredBatchCallAdapterContract';
import { createAsyncAtom, emitAtomEvent } from '../../lib/state';
import { pauseBalanceRefresh, resumeBalanceRefresh } from './pauseBalanceRefresh';

interface AdjustRequest {
  asset: Asset;
  amount: Decimal;
  accountId: number;
  isFromInterestBearing: boolean;
  isFromCollateral: boolean;
  isToInterestBearing: boolean;
  isToCollateral: boolean;
  txnId: string;
  provider: RpcProvider;
}

interface AdjustStatus {
  pending: boolean;
  request: AdjustRequest;
  success?: boolean;
  error?: Error;
  getTransactionResponse?: InvokeResponse;
  transactionData?: {
    burnAndMintedAmount: Decimal;
  };
  txnId: string;
}

/* interface BurnAndMintResponse {
  request: BurnAndMintRequest;
  getTransactionResponse?: InvokeTransactionResponse | void;
  getTransactionReceipt?: InvokeTransactionReceiptResponse;
  transactionData?: {
    burnAndMintedAmount: Decimal;
    gasFee: Decimal;
  };
  error?: Error;
  txnId: string;
} */

const lendAndBorrowAdjustStatusAtom = createAsyncAtom<Nullable<AdjustStatus>>(async () => null);

const lendAndBorrowAdjust = async (walletAccount: AccountInterface, request: AdjustRequest) => {
  const {
    asset,
    amount,
    accountId,
    isFromInterestBearing,
    isFromCollateral,
    isToInterestBearing,
    isToCollateral,
    txnId,
    provider,
  } = request;

  const deferredBatchCallAdapterAddress = getConfigManager().getConfig().deferredBatchCallAdapter;

  const deferredBatchCallAdapterContract = new DeferredBatchCallAdapterContract(
    deferredBatchCallAdapterAddress,
    walletAccount,
  );

  const tokenPrecision = getConfigManager().getTokenPrecision(asset.address);

  const amountFormatted = DecimalFormat.format(amount, {
    style: 'currency',
    currency: asset.token,
    fractionDigits: asset.uiTokenPrecision,
    pad: true,
  });

  pauseBalanceRefresh();
  try {
    lendAndBorrowAdjustStatusAtom.setValue({
      pending: true,
      request,
      txnId,
    });

    let transactionResponse: Nullable<InvokeResponse> = null;
    try {
      transactionResponse = await deferredBatchCallAdapterContract.burnAndMint(
        asset,
        tokenPrecision,
        amount,
        accountId,
        isFromInterestBearing,
        isFromCollateral,
        isToInterestBearing,
        isToCollateral,
        walletAccount,
      );
    } catch (error) {
      lendAndBorrowAdjustStatusAtom.setValue({
        pending: false,
        success: false,
        error: new Error('Failed to adjust!'),
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Adjust ${amountFormatted}`,
      });

      return;
    }

    addToast({
      id: uuid(),
      type: 'info',
      message: `Adjust ${amountFormatted}`,
      txId: transactionResponse?.transactionHash,
    });

    if (!transactionResponse) {
      const userRejectError = new Error('Rejected by user.');
      lendAndBorrowAdjustStatusAtom.setValue({
        pending: false,
        success: false,
        error: userRejectError,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Adjust ${amountFormatted}`,
      });

      return;
    }

    const transactionReceipt = await waitForTransactionReceipt(
      transactionResponse.transactionHash,
      walletAccount,
      provider,
    );

    if (!transactionReceipt) {
      const receiptFetchFailed = new Error('Failed to fetch adjust transaction receipt!');
      lendAndBorrowAdjustStatusAtom.setValue({
        pending: false,
        success: false,
        error: receiptFetchFailed,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Adjust ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });

      return;
    }

    if (transactionReceipt.isReverted()) {
      const txnReverted = new Error('Transaction reverted!');
      lendAndBorrowAdjustStatusAtom.setValue({
        pending: false,
        success: false,
        error: txnReverted,
        request,
        txnId,
      });

      addToast({
        id: uuid(),
        type: 'error',
        message: `Adjust ${amountFormatted}`,
        txId: transactionResponse.transactionHash,
      });
    }

    lendAndBorrowAdjustStatusAtom.setValue({
      pending: false,
      success: true,
      request,
      getTransactionResponse: transactionResponse,
      transactionData: {
        burnAndMintedAmount: request.amount,
      },
      txnId,
    });

    emitAtomEvent('lend-and-borrow-adjust');

    addToast({
      id: uuid(),
      type: 'success',
      message: `Adjusted **${amountFormatted}**`,
      txId: transactionResponse.transactionHash,
    });
  } finally {
    resumeBalanceRefresh();
  }
};

export { lendAndBorrowAdjustStatusAtom, lendAndBorrowAdjust };
